/**
 * Internal strategy code → user-facing label.
 *
 * The trading engine tags every trade with a code (``S1``, ``fib``,
 * ``pump``, ``manual``) and — for ``S1`` — a ``setup``
 * discriminator (``lead``/``trend``/``continuation``/``reversion``/
 * ``exhaustion``). None of these mean anything to a user, so the
 * notification surface renders the bot's-voice phrase instead.
 *
 * Resolution: ``S1`` + a known ``setup`` → setup phrase; otherwise the
 * strategy phrase; otherwise the upper-cased raw code (so a brand-new
 * strategy still renders *something* until this map catches up).
 *
 * i18n keys live under ``strategy_label.*`` / ``side_label.*`` in
 * ``i18n/ru.json`` / ``i18n/en.json``. Mirror of the tgbot map in
 * ``apps/tgbot/.../realtime_notify.py``.
 */

type T = (key: string, opts?: Record<string, unknown>) => string;

const S1_SETUPS = new Set([
  'lead',
  'trend',
  'continuation',
  'reversion',
  'exhaustion',
]);

const KNOWN_STRATEGIES = new Set(['s1', 'fib', 'pump', 'manual']);

export function strategyLabel(
  strategy: string | null | undefined,
  setup: string | null | undefined,
  t: T,
): string {
  const raw = (strategy ?? '').trim();
  const code = raw.toLowerCase();
  const setupKey = (setup ?? '').trim().toLowerCase();
  if (code === 's1' && S1_SETUPS.has(setupKey)) {
    return t(`strategy_label.s1_${setupKey}`);
  }
  if (KNOWN_STRATEGIES.has(code)) {
    return t(`strategy_label.${code}`);
  }
  return raw.toUpperCase();
}

export function sideLabel(side: string | null | undefined, t: T): string {
  const s = (side ?? '').trim().toUpperCase();
  if (s === 'UP' || s === 'LONG' || s === 'BUY') return t('side_label.up');
  if (s === 'DOWN' || s === 'SHORT' || s === 'SELL') return t('side_label.down');
  return s;
}
